import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

const DomainSettingsPage = () => {
  const { tenant } = useTenant();
  const { isTenantAdmin } = useAccessControl();
  const [customDomain, setCustomDomain] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pendingAction, setPendingAction] = useState<"save" | "remove" | null>(null);
  const isSaving = pendingAction !== null;

  useEffect(() => {
    if (tenant) {
      setCustomDomain(tenant.customDomain || "");
    }
  }, [tenant]);

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
            <p className="text-gray-600">You need Tenant Admin permissions to access this page.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setPendingAction("save");

    try {
      await api.patch(`/tenants/${tenant?.id}/domain`, {
        customDomain: customDomain.trim() || null,
      });

      setSuccess("Custom domain updated successfully!");

      // Reload tenant data
      // In a real implementation, you'd refetch tenant data
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update domain";
      setError(errorMessage);
    } finally {
      setPendingAction(null);
    }
  };

  const handleRemove = async () => {
    if (!confirm("Are you sure you want to remove the custom domain?")) {
      return;
    }

    setError("");
    setSuccess("");
    setPendingAction("remove");

    try {
      await api.patch(`/tenants/${tenant?.id}/domain`, {
        customDomain: null,
      });

      setSuccess("Custom domain removed successfully!");
      setCustomDomain("");

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to remove domain";
      setError(errorMessage);
    } finally {
      setPendingAction(null);
    }
  };

  // Get subdomain URL
  const tenantDomainSuffix = import.meta.env.VITE_TENANT_DOMAIN_SUFFIX || "philolms.com";
  const subdomainUrl = tenant ? `https://${tenant.slug}.${tenantDomainSuffix}` : "";

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title="Domain Settings"
          description="Configure your library's custom domain. Users can access your library using either the default subdomain or your custom domain."
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Domain" }]}
          showSearch={false}
          titleSize="md"
        />

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Domain Configuration</h2>
          <p className="text-sm text-gray-600 mb-6">
            Configure your library's custom domain. Users can access your library using either the
            default subdomain or your custom domain.
          </p>

          {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}

          {success && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded text-sm">{success}</div>
          )}

          <form onSubmit={handleSave}>
            <div className="mb-6">
              <Label htmlFor="subdomain" className="block mb-2">
                Default Subdomain (Read-only)
              </Label>
              <Input
                id="subdomain"
                type="text"
                value={subdomainUrl}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                This is your default library URL. It cannot be changed.
              </p>
            </div>

            <div className="mb-6">
              <Label htmlFor="customDomain" className="block mb-2">
                Custom Domain (Optional)
              </Label>
              <Input
                id="customDomain"
                type="text"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                placeholder="library.example.com"
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter your custom domain (e.g., library.example.com). DNS validation will be added
                in a future update.
              </p>
            </div>

            <div className="flex gap-4">
              <Button type="submit" disabled={isSaving} className="gap-2">
                {pendingAction === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save Domain
              </Button>
              {tenant?.customDomain && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleRemove}
                  disabled={isSaving}
                  className="gap-2"
                >
                  {pendingAction === "remove" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Remove Custom Domain
                </Button>
              )}
            </div>
          </form>

          <div className="mt-8 p-4 bg-primary/5 rounded-lg">
            <h3 className="font-semibold mb-2">Domain Setup Instructions</h3>
            <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
              <li>Purchase a domain from a domain registrar</li>
              <li>Configure DNS settings (instructions will be provided)</li>
              <li>Enter your custom domain above</li>
              <li>DNS validation will be performed automatically</li>
            </ol>
            <p className="text-xs text-gray-600 mt-3">
              Note: DNS validation and SSL automation are coming soon. For now, domain format
              validation is performed.
            </p>
          </div>
        </Card>
      </PageContainer>
    </AppLayout>
  );
};

export default DomainSettingsPage;
